import { updateBusinessEntity } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  Textarea,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotification } from '../../../../../hooks/useNotification';

const editBusinessEntityDescriptionSchema = z.object({
  businessDescription: z
    .string()
    .trim()
    .min(25, { message: 'Description must be at least 25 characters long' }),
});

type EditBusinessEntityDescriptionSchemaFields = z.infer<
  typeof editBusinessEntityDescriptionSchema
>;

export const EditBusinessEntityDescriptionModal = ({
  isOpen,
  onClose,
  businessEntity,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntity: IBusinessEntity;
  onSuccess?: () => void;
}) => {
  const {
    watch,
    setValue,
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditBusinessEntityDescriptionSchemaFields>({
    resolver: zodResolver(editBusinessEntityDescriptionSchema),
    defaultValues: {
      businessDescription:
        businessEntity.generalCorporationInfo.businessDescription,
    },
  });

  useEffect(() => {
    setValue(
      'businessDescription',
      businessEntity.generalCorporationInfo.businessDescription
    );
  }, [businessEntity.generalCorporationInfo.businessDescription]);

  const { setNotification } = useNotification();

  const close = () => {
    onClose();
    reset();
  };

  const onSave = async () => {
    if (isSubmitting) return;

    const description = watch('businessDescription');

    const res = await updateBusinessEntity(businessEntity.id, {
      businessDescription: description.trim(),
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update business description',
      });
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Business description updated',
    });

    if (onSuccess) await onSuccess();
    close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader>
          <DialogTitle>
            {businessEntity?.generalCorporationInfo?.businessDescription?.trim()
              ?.length
              ? 'Edit'
              : 'Add'}{' '}
            Business Description
          </DialogTitle>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label>
              <span>Business description</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <Textarea
              id="businessDescription"
              {...register('businessDescription')}
              error={errors.businessDescription?.message}
            />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit(onSave)}
            disabled={isSubmitting}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
